import { useQuery } from "@apollo/client/react";
import { createFileRoute } from "@tanstack/react-router";
import { useMediaQuery } from "@uidotdev/usehooks";
import { Suspense, lazy, useEffect, useState } from "react";
import type {
  GetShowQuery,
  GetShowQueryVariables,
} from "@/codegen/graphql/generated/graphql";
import { GET_SHOW } from "@/codegen/graphql/queries/GetShows.query";
import EpisodesPlayer from "@/modules/EpisodesPlayer/EpisodesPlayer";
import { isNewPlayerVariant } from "@/modules/NewEpisodesPlayer/isNewPlayerVariant";
import { useAuthStore } from "@/stores/useAuth";
import {
  useSetCurrentEpisode,
  useSetCurrentShow,
} from "@/modules/EpisodesPlayer/hooks/useCurrentEpisode";
import NotFound from "@/components/NotFound";
import EpisodesPlayerSkeleton from "@/modules/EpisodesPlayer/EpisodesPlayerSkeleton";
import { useTracking } from "@/components/ClientAnalytics";
import { getUTMParams } from "@/lib/utmTracking";
import useShowMetaTags from "@/hooks/useShowMetaTags";
import { loadShowOrNotFound } from "@/lib/loadShowOrNotFound";

// Its own chunk: the control never loads it (SHO-815).
const NewEpisodesPlayer = lazy(() => import("@/modules/NewEpisodesPlayer"));

export const Route = createFileRoute("/_videoPlayer/$series/$episode")({
  // A 404 on a malformed param or an unknown show, and the tags applied
  // before resolution — see `loadShowOrNotFound` for the why of each.
  loader: ({ params, preload, location }) =>
    loadShowOrNotFound(GET_SHOW, params.series, {
      preload,
      location,
      target: "player",
    }),
  notFoundComponent: NotFound,
  // No delay: the loader above now gates route resolution on the show fetch,
  // so without this the previous route would sit frozen with no feedback
  // instead of the skeleton shown immediately before this change.
  pendingMs: 0,
  pendingMinMs: 0,
  pendingComponent: EpisodesPlayerSkeleton,
  component: Episode,
});

function Episode() {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { trackAppsFlyerEvent } = useTracking();
  // Read once, off the store: `useAmplitudeExperiment` rewrites the stored
  // variants after its fetch lands, seconds into the session, and a live read
  // would swap the player under a visitor mid-episode (restarting it from 0).
  const [isNewPlayer] = useState(() =>
    isNewPlayerVariant(useAuthStore.getState()),
  );

  const { series, episode } = Route.useParams();
  const { showId } = Route.useLoaderData();
  const { data, loading } = useQuery<GetShowQuery, GetShowQueryVariables>(
    GET_SHOW,
    { variables: { showId } },
  );

  const episodeId = episode === "0" ? data?.show.episodes[0]?.id : episode;

  const setCurrentShow = useSetCurrentShow();
  const setCurrentEpisode = useSetCurrentEpisode();

  useEffect(() => {
    if (data?.show) setCurrentShow(data.show);
    if (data?.show.episodes.length && data.show.episodes[0])
      setCurrentEpisode(data.show.episodes[0]);
  }, [data?.show]);

  useEffect(() => {
    if (data?.show && getUTMParams().utm_source)
      trackAppsFlyerEvent({
        eventName: "landing page view",
        eventValue: {
          content_name: [data.show.id],
        },
      });
  }, [data?.show]);

  useShowMetaTags(data?.show, "player");

  if (loading) return <EpisodesPlayerSkeleton />;

  if (!data?.show) return <NotFound />;

  if (isNewPlayer)
    return (
      <Suspense fallback={<EpisodesPlayerSkeleton />}>
        <NewEpisodesPlayer
          episodes={data.show.episodes}
          show={data.show}
          episodeId={episodeId ?? ""}
          series={series}
        />
      </Suspense>
    );

  return (
    <EpisodesPlayer
      episodes={data.show.episodes}
      show={data.show}
      episodeId={episodeId ?? ""}
      isMobile={isMobile}
      series={series}
    />
  );
}
